import { useState } from 'react';
import { BarChart2, BookOpen, CheckCircle, FileText, Lock } from 'lucide-react';
import { Link } from 'wouter';
import { computeOverallScore, getHpGrade } from '../../lib/assessment/content';
import { routes } from '../../lib/assessment/routes';
import type { AssessmentType, FreeResult } from '../../lib/apiClient';
import { Button } from '../ui/button';
import { CheckoutButton } from '../report/CheckoutButton';

/**
 * The dashboard's **Reports** tab, copied from production.
 *
 * A filter strip, then a card per completed assessment: the type, whether the
 * full report is unlocked, the primary type, the date, the HP Score for
 * Bottleneck, the surfaced bottleneck chips, and the actions.
 *
 * PARITY TODO: production decides "unlocked" from completed purchases plus
 * consumed report credits. V2 reads `results.is_unlocked`, which the atomic
 * fulfilment RPC sets in the same transaction that records those very rows —
 * the same fact, established one step closer to the report.
 */
const FILTERS = ['all', 'bottleneck', 'workfit', 'perspective'] as const;

type Filter = (typeof FILTERS)[number];

const FILTER_LABELS: Record<Filter, string> = {
  all: 'All',
  bottleneck: 'Bottleneck',
  workfit: 'Work Fit',
  perspective: 'Perspective',
};

/** Production's filter keys are not V2's assessment types: `workfit` vs `work_fit`. */
const FILTER_OF: Record<AssessmentType, Exclude<Filter, 'all'>> = {
  bottleneck: 'bottleneck',
  work_fit: 'workfit',
  perspective: 'perspective',
};

/** Production's card eyebrow: `{label} Assessment`. */
const TYPE_LABELS: Record<AssessmentType, string> = {
  bottleneck: 'Bottleneck',
  work_fit: 'Work Fit',
  perspective: 'Perspective',
};

/** Production's chips on a Bottleneck card. */
const BOTTLENECK_LABELS: Record<string, string> = {
  avoidance: 'Avoidance',
  emotionalRegulation: 'Emotional Regulation',
  focus: 'Focus',
  goalClarity: 'Goal Clarity',
  identityPressure: 'Identity Pressure',
  isolation: 'Isolation',
  overextension: 'Overextension',
  recovery: 'Recovery',
};

/** Work Fit has no workbook. A link here would 404 for someone who has paid. */
const HAS_WORKBOOKS: Record<AssessmentType, boolean> = {
  bottleneck: true,
  work_fit: false,
  perspective: true,
};

export function ReportsTab({ assessments }: { assessments: FreeResult[] }) {
  const [filter, setFilter] = useState<Filter>('all');

  const filtered = assessments.filter(
    (a) => filter === 'all' || FILTER_OF[a.assessmentType] === filter,
  );

  return (
    <div className="space-y-6 pt-6" data-testid="dashboard-panel-reports">
      {/* Filter strip */}
      <div className="flex gap-2 flex-wrap" data-testid="report-filters">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            data-testid={`report-filter-${f}`}
            aria-pressed={filter === f}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
              filter === f
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {FILTER_LABELS[f]}
          </button>
        ))}
      </div>

      {/* Production distinguishes "you have none" from "none match this filter". */}
      {filtered.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground text-sm" data-testid="reports-empty">
          {assessments.length === 0
            ? 'No assessments completed yet. Take your first assessment to see results here.'
            : 'No results for this filter.'}
        </div>
      ) : null}

      <div className="space-y-4">
        {filtered.map((a) => (
          <ReportCard key={a.id} assessment={a} />
        ))}
      </div>
    </div>
  );
}

function ReportCard({ assessment: a }: { assessment: FreeResult }) {
  const isPaid = a.isUnlocked;
  const hpScore = a.assessmentType === 'bottleneck' ? Math.round(computeOverallScore(a)) : null;
  const { grade, label: gradeLabel } =
    hpScore !== null ? getHpGrade(hpScore) : { grade: '', label: '' };

  return (
    <div
      className="p-6 rounded-xl border border-border bg-card flex flex-col md:flex-row md:items-start justify-between gap-4"
      data-testid="report-card"
      data-result-id={a.id}
      data-assessment-type={a.assessmentType}
    >
      <div className="flex-1 space-y-2 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest shrink-0">
            {TYPE_LABELS[a.assessmentType]} Assessment
          </span>
          {isPaid ? (
            <span
              className="inline-flex items-center rounded-md border border-green-600/30 bg-green-600/20 px-2 py-0.5 text-xs text-green-400 shrink-0"
              data-testid="report-unlocked"
            >
              <CheckCircle className="w-3 h-3 mr-1" aria-hidden="true" />
              Full Report Unlocked
            </span>
          ) : (
            <span
              className="inline-flex items-center rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground shrink-0"
              data-testid="report-locked"
            >
              <Lock className="w-3 h-3 mr-1" aria-hidden="true" />
              Free Results Only
            </span>
          )}
        </div>

        <p className="text-base font-bold text-primary truncate" data-testid="report-primary-type">
          {a.primaryType}
        </p>

        <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
          <span data-testid="report-date">{longDate(a.completedAt)}</span>
          {hpScore !== null ? (
            <>
              <span className="text-border">|</span>
              <span data-testid="report-hp-score">
                HP Score: <span className="text-primary font-bold">{hpScore}</span> ({grade},{' '}
                {gradeLabel})
              </span>
            </>
          ) : null}
        </div>

        {a.assessmentType === 'bottleneck' && a.topCategories.length > 0 ? (
          <div className="flex flex-wrap gap-2 pt-1" data-testid="report-bottlenecks">
            {a.topCategories.map((b) => (
              <span
                key={b}
                className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground"
              >
                {BOTTLENECK_LABELS[b] ?? b}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-2 md:min-w-[180px] shrink-0">
        <Link
          href={routes.result(a.id)}
          data-testid="report-view-free"
          className="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <Button variant="outline" className="w-full gap-2" tabIndex={-1}>
            <BarChart2 className="w-4 h-4" aria-hidden="true" />
            View Free Result
          </Button>
        </Link>

        {isPaid ? (
          <>
            <Link
              href={routes.report(a.id)}
              data-testid="report-open"
              className="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <Button className="w-full gap-2" tabIndex={-1}>
                <FileText className="w-4 h-4" aria-hidden="true" />
                Open Report
              </Button>
            </Link>

            {HAS_WORKBOOKS[a.assessmentType] ? (
              <>
                <Link
                  href={routes.reportWorkbook(a.id, '7-day')}
                  data-testid="report-workbook-7-day"
                  className="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <Button variant="outline" className="w-full gap-2" tabIndex={-1}>
                    <BookOpen className="w-4 h-4" aria-hidden="true" />
                    7-Day Workbook
                  </Button>
                </Link>
                <Link
                  href={routes.reportWorkbook(a.id, '30-day')}
                  data-testid="report-workbook-30-day"
                  className="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <Button variant="outline" className="w-full gap-2" tabIndex={-1}>
                    <BookOpen className="w-4 h-4" aria-hidden="true" />
                    30-Day Workbook
                  </Button>
                </Link>
              </>
            ) : null}
          </>
        ) : (
          <CheckoutButton resultId={a.id} />
        )}
      </div>
    </div>
  );
}

/**
 * Production formats these with date-fns as `MMMM d, yyyy`. The locale call
 * below produces the same string without adding a dependency for one line.
 */
function longDate(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}
